import { useState, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useInventoryDashboardLayoutStore } from '../stores/inventoryDashboardLayoutStore'
import {
    Chart as ChartJS, CategoryScale, LinearScale,
    LineElement, LineController, PointElement, Tooltip, Legend, Filler,
    ArcElement, PieController
} from 'chart.js'
import { api, apiPost } from '../lib/api'
import { fmt } from '../lib/dashboardHelpers'
import { LIVE, resolveSnapshot, snapshotFigures, type InventoryLevelPoint, type SnapshotOption } from '../lib/inventorySnapshots'
import type { StoreConfig } from '../types/dashboard'
import InventoryLevelChart from '../components/dashboard/InventoryLevelChart'
import DepartmentPieChart from '../components/dashboard/DepartmentPieChart'
import StockoutInsightsPanel from '../components/dashboard/StockoutInsightsPanel'
import WidgetGrid from '../components/dashboard/WidgetGrid'
import { SqlDebugModal } from '../components/dashboard'
import WidgetGallery from '../components/WidgetGallery'
import LayoutPresetsMenu from '../components/dashboard/LayoutPresetsMenu'
import { useLayoutManager } from '../components/dashboard/useLayoutManager'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import '../styles/dashboard.css'
import '../styles/widget-gallery.css'

ChartJS.register(CategoryScale, LinearScale, LineElement, LineController, PointElement, Tooltip, Legend, Filler, ArcElement, PieController)

interface Brand { id: string; name: string }
interface StockoutItem {
    itemCardId: number; description: string; styleCode: string; brand: string; color: string; size: string
    priorQty: number; unitsSold60d: number; revenue60d: number; availableElsewhere: boolean
    centralAts: number | null; stockoutDate: string; severity: 'critical' | 'warning'
}

function pctChange(curr: number, prev: number): number {
    if (prev === 0) return curr > 0 ? 100 : 0
    return ((curr - prev) / Math.abs(prev)) * 100
}
function getGreeting(): string {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
}

export default function InventoryDashboardPage() {
    const user = useAuthStore(s => s.user)
    const token = useAuthStore(s => s.accessToken)
    const isAdmin = ['HeadOffice', 'SuperAdmin'].includes(user?.role ?? '')
    const qc = useQueryClient()

    const {
        layouts, activeWidgets, widgetSettings, isEditing, layoutVersion,
        currentLayoutId,
        setEditing, removeWidget, setLayouts, snapshotLayouts,
        revertToInitial, updateWidgetSettings,
        loadFromServer, loadLayout, saveAsSystemDefault,
        applyImportedLayout,
    } = useInventoryDashboardLayoutStore()

    const { data: storeConfig, isLoading: configLoading } = useQuery<StoreConfig>({
        queryKey: ['dashboard-stores'],
        queryFn: () => api('/api/dashboard/stores', token),
        staleTime: 60_000,
    })

    const [searchParams, setSearchParams] = useSearchParams()

    const [storeId, setStoreId] = useState<string>(searchParams.get('storeId') || 'group')
    const [brandId, setBrandId] = useState<string>(searchParams.get('brandId') || '')
    const [department, setDepartment] = useState<string>(searchParams.get('department') || '')
    const [subdept, setSubdept] = useState<string>(searchParams.get('subdept') || '')
    const [gender, setGender] = useState<string>(searchParams.get('gender') || '')
    const [selectedSnapshot, setSelectedSnapshot] = useState<string>(searchParams.get('snapshot') || '')
    const [snapshotLimit, setSnapshotLimit] = useState<string>(searchParams.get('limit') || 'ALL')
    const includeLive = true

    const [showDebug, setShowDebug] = useState(false)
    const [actionMenuOpen, setActionMenuOpen] = useState(false)
    const [showGallery, setShowGallery] = useState(false)
    const [overwriteSystemDefault, setOverwriteSystemDefault] = useState(false)
    const [overwriteUserCustomizations, setOverwriteUserCustomizations] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState<string | null>(null)

    const greeting = getGreeting()

    useEffect(() => {
        if (!storeConfig) return
        // A single-store, non-privileged user has no "group"/"all" option to represent in the picker,
        // so bind directly to their store — otherwise the <select> would display that store while the
        // state stays "group", and the queries would silently run unscoped.
        if (!storeConfig.canViewAll && storeConfig.stores.length === 1) {
            setStoreId(storeConfig.stores[0].id)
        } else {
            setStoreId(storeConfig.defaultView)
        }
    }, [storeConfig])
    // Load layout from server
    useEffect(() => {
        if (token && user) {
            const requestedLayoutId = new URLSearchParams(window.location.search).get('layout')
            if (requestedLayoutId) {
                // loadLayout doesn't fetch the presets list; loadFromServer then fills it in
                // (and skips the layout itself, which is loaded by then)
                loadLayout(token, user.id, 'inventory', requestedLayoutId).then(() => loadFromServer(token, user.id, 'inventory'))
            } else {
                loadFromServer(token, user.id, 'inventory')
            }
        }
    }, [token, user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync state to URL
    useEffect(() => {
        const params = new URLSearchParams()
        if (currentLayoutId) params.set('layout', currentLayoutId)
        if (storeId !== 'group') params.set('storeId', storeId)
        if (brandId) params.set('brandId', brandId)
        if (department) params.set('department', department)
        if (subdept) params.set('subdept', subdept)
        if (gender) params.set('gender', gender)
        if (selectedSnapshot) params.set('snapshot', selectedSnapshot)
        if (snapshotLimit !== 'ALL') params.set('limit', snapshotLimit)
        
        setSearchParams(params, { replace: true })
    }, [currentLayoutId, storeId, brandId, department, subdept, gender, selectedSnapshot, snapshotLimit, setSearchParams])


    const filterParams = useMemo(() => {
        const p = new URLSearchParams()
        // Always send the literal selection (guid | "group" | "all"); the API resolves it
        // against the user's role + store assignments and enforces authorization.
        p.set('storeId', storeId || 'group')
        if (brandId) p.set('brandId', brandId)
        if (department) p.set('department', department)
        if (subdept) p.set('subdept', subdept)
        if (gender) p.set('gender', gender)
        return p.toString()
    }, [storeId, brandId, department, subdept, gender])

    const isLiveSelected = selectedSnapshot === LIVE
    const snapshotParam = selectedSnapshot && !isLiveSelected ? `&snapshotDate=${selectedSnapshot}` : ''
    const liveParam = includeLive ? '&includeLive=true' : ''
    const liveModeParam = isLiveSelected ? '&snapshotMode=LIVE' : ''

    const { data: snapshots } = useQuery<SnapshotOption[]>({
        queryKey: ['inventory-snapshots', includeLive], queryFn: () => api(`/api/inventory/snapshots?${liveParam}`, token), staleTime: 300_000,
    })

    // Keep the selection to a snapshot the picker offers: the first (Live) when there is none,
    // and for a day it doesn't list (an old link), the snapshot the API would anchor that day on.
    useEffect(() => {
        if (!snapshots || snapshots.length === 0) return
        const resolved = resolveSnapshot(selectedSnapshot, snapshots)
        if (resolved !== selectedSnapshot) setSelectedSnapshot(resolved)
    }, [snapshots, selectedSnapshot])
    // The widgets that query by snapshot wait for a resolved one, instead of first fetching with
    // none (the latest snapshot's figures) and then again for the selection.
    const snapshotReady = !!snapshots?.some(s => s.date === selectedSnapshot)

    // Generate months options dynamically: ALL (maxMonths), multiples of 12 down to 24, then 18, 12, 6
    const showOptions = useMemo(() => {
        if (!snapshots) return [{ value: 'ALL', label: 'ALL' }, { value: '12', label: '12' }]
        const maxMonths = snapshots.filter(s => s.date !== 'LIVE').length
        
        const options: { value: string; label: string }[] = []
        options.push({ value: 'ALL', label: `ALL (${maxMonths})` })
        
        // Find highest multiple of 12 that is <= maxMonths
        const start12 = Math.floor(maxMonths / 12) * 12
        for (let val = start12; val >= 24; val -= 12) {
            if (val <= maxMonths) {
                options.push({ value: String(val), label: String(val) })
            }
        }
        
        // Push final three increments: 18, 12, 6
        const finalThree = [18, 12, 6]
        for (const val of finalThree) {
            if (val < maxMonths) {
                options.push({ value: String(val), label: String(val) })
            }
        }
        
        return options
    }, [snapshots])

    // Defensive fallback: Reset selected limit to 'ALL' if it's no longer present in the dynamically calculated options
    useEffect(() => {
        const optionValues = showOptions.map(o => o.value)
        if (snapshotLimit !== 'ALL' && optionValues.length > 1 && !optionValues.includes(snapshotLimit)) {
            setSnapshotLimit('ALL')
        }
    }, [showOptions, snapshotLimit])

    const { data: brands } = useQuery<Brand[]>({
        queryKey: ['inventory-brands', storeId, department, subdept, gender, selectedSnapshot, includeLive],
        queryFn: () => api(`/api/inventory/brands?${filterParams}${snapshotParam}${liveModeParam}`, token), staleTime: 300_000,
        enabled: snapshotReady,
    })
    const { data: departments } = useQuery<string[]>({
        queryKey: ['inventory-departments'], queryFn: () => api('/api/inventory/departments', token), staleTime: 300_000,
    })
    const { data: subdepartments } = useQuery<string[]>({
        queryKey: ['inventory-subdepts', department],
        queryFn: () => api(`/api/inventory/subdepartments${department ? `?department=${encodeURIComponent(department)}` : ''}`, token),
        staleTime: 300_000,
    })
    const { data: genders } = useQuery<string[]>({
        queryKey: ['inventory-genders', department],
        queryFn: () => api(`/api/inventory/genders${department ? `?department=${encodeURIComponent(department)}` : ''}`, token),
        staleTime: 300_000,
    })
    const { data: levels, isFetching: levelsLoading, dataUpdatedAt } = useQuery<InventoryLevelPoint[]>({
        queryKey: ['inventory-levels', storeId, brandId, department, subdept, gender, includeLive],
        queryFn: () => api(`/api/inventory/levels?${filterParams}${liveParam}`, token),
        staleTime: 60_000, placeholderData: keepPreviousData
    })

    const visibleLevels = useMemo(() => {
        if (!levels || levels.length === 0) return []
        if (snapshotLimit === 'ALL') return levels

        const limitVal = parseInt(snapshotLimit, 10)
        if (isNaN(limitVal) || limitVal >= levels.length) return levels

        const activeIdx = levels.findIndex(pt => pt.date === selectedSnapshot)

        if (activeIdx === -1) {
            // Default to showing the latest limitVal months
            return levels.slice(levels.length - limitVal)
        }

        let startIdx = activeIdx - Math.floor(limitVal / 2)
        if (startIdx < 0) {
            startIdx = 0
        } else if (startIdx + limitVal > levels.length) {
            startIdx = Math.max(0, levels.length - limitVal)
        }

        return levels.slice(startIdx, startIdx + limitVal)
    }, [levels, snapshotLimit, selectedSnapshot])
    // The KPI tiles show the selected point of the chart's own series, so the tiles and the chart
    // can't disagree about which snapshot they show or what it holds.
    const figures = useMemo(
        () => (levels && snapshotReady ? snapshotFigures(levels, selectedSnapshot) : undefined),
        [levels, snapshotReady, selectedSnapshot])
    const { data: stockouts, isLoading: stockoutsLoading } = useQuery<StockoutItem[]>({
        queryKey: ['inventory-stockouts', filterParams, selectedSnapshot, includeLive],
        queryFn: () => api(`/api/inventory/stockouts?${filterParams}${snapshotParam}${liveModeParam}`, token), placeholderData: keepPreviousData,
        enabled: snapshotReady,
    })
    const { data: deptBreakdown, isFetching: deptLoading } = useQuery<any[]>({
        queryKey: ['inventory-dept-breakdown', storeId, brandId, gender, selectedSnapshot, includeLive],
        queryFn: () => api(`/api/inventory/department-breakdown?${filterParams}${snapshotParam}${liveModeParam}`, token),
        staleTime: 60_000, placeholderData: keepPreviousData,
        enabled: snapshotReady,
    })

    const { data: debugData } = useQuery<any>({
        queryKey: ['inventory-debug', filterParams, selectedSnapshot],
        queryFn: () => api(`/api/inventory/debug-sql?${filterParams}${snapshotParam}`, token),
        enabled: showDebug
    })

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['inventory-levels'] })
        qc.invalidateQueries({ queryKey: ['inventory-stockouts'] })
        qc.invalidateQueries({ queryKey: ['inventory-dept-breakdown'] })
    }
    const handleFlushCache = async () => {
        try { await apiPost('/api/inventory/cache/flush', token); refresh() } catch { /* silent */ }
    }

    // The chart hands back the clicked point's date, which is its value in the snapshot picker.
    const handleSelectSnapshotFromChart = (date: string) => setSelectedSnapshot(date)

    // ── Edit mode handlers ──────────────────────────────────────────
    const handleCancel = () => { revertToInitial() }
    // 💾 Save, Save as…, Revert and the Layouts menu — shared with the other dashboards. Inventory's
    // admin options ride on the dialog's Save button rather than a button of their own.
    const layoutUi = useLayoutManager(useInventoryDashboardLayoutStore, 'inventory', {
        onOpenSaveDialog: () => { setOverwriteSystemDefault(false); setOverwriteUserCustomizations(false) },
        afterDialogSave: async () => {
            if (overwriteSystemDefault) await saveAsSystemDefault(token!, 'inventory', overwriteUserCustomizations)
        },
        renderAdmin: isAdmin ? () => (<>
            <div className="admin-save-divider" />
            <h4 style={{ marginBottom: '0.5rem' }}>Admin Options</h4>
            <label className="admin-save-checkbox"><input type="checkbox" checked={overwriteSystemDefault} onChange={e => { setOverwriteSystemDefault(e.target.checked); if (!e.target.checked) setOverwriteUserCustomizations(false) }} /> Overwrite System Default</label>
            <label className="admin-save-checkbox" style={{ marginTop: '8px', opacity: overwriteSystemDefault ? 1 : 0.5 }}><input type="checkbox" checked={overwriteUserCustomizations} onChange={e => { if (overwriteSystemDefault) setOverwriteUserCustomizations(e.target.checked) }} /> Also overwrite user customizations</label>
        </>) : undefined,
    })
    const handleExportLayout = () => JSON.stringify({ layouts, activeWidgets, widgetSettings }, null, 2)
    const handleImportLayout = (json: string): string | null => {
        let parsed: any
        try { parsed = JSON.parse(json) } catch { return 'Invalid JSON syntax.' }
        if (!parsed.activeWidgets || !Array.isArray(parsed.activeWidgets)) return 'Missing "activeWidgets" array.'
        if (!parsed.layouts || typeof parsed.layouts !== 'object') return 'Missing "layouts" object.'
        applyImportedLayout(parsed); return null
    }

    // ── KPI helper with prior month + last year comparisons ────────
    const renderKpi = (label: string, value: number, priorValue: number, lyValue: number, accent: string, format: 'int' | 'currency' = 'int') => {
        const pctMom = pctChange(value, priorValue)
        const dirMom = pctMom > 0 ? 'up' : pctMom < 0 ? 'down' : 'flat'
        const pctLy = pctChange(value, lyValue)
        const dirLy = pctLy > 0 ? 'up' : pctLy < 0 ? 'down' : 'flat'
        return (
            <div className="kpi-card" style={{ '--kpi-accent': accent } as any}>
                <div className="kpi-label">{label}</div>
                <div className="kpi-value">{fmt(value, format)}</div>
                <div className="kpi-comparisons">
                    <div className="kpi-comparison">
                        <span className={`kpi-arrow ${dirMom}`}>{dirMom === 'up' ? '▲' : dirMom === 'down' ? '▼' : '–'}</span>
                        <span className={`kpi-pct ${dirMom}`}>{Math.abs(pctMom).toFixed(1)}%</span>
                        <span className="comp-label-full">vs prior month</span>
                        <span className="comp-label-short">m</span>
                        <span className="comp-val" style={{ color: 'var(--color-text-tertiary)' }}>({fmt(priorValue, format)})</span>
                    </div>
                    <div className="kpi-comparison">
                        <span className={`kpi-arrow ${dirLy}`}>{dirLy === 'up' ? '▲' : dirLy === 'down' ? '▼' : '–'}</span>
                        <span className={`kpi-pct ${dirLy}`}>{Math.abs(pctLy).toFixed(1)}%</span>
                        <span className="comp-label-full">vs last year</span>
                        <span className="comp-label-short">y</span>
                        <span className="comp-val" style={{ color: 'var(--color-text-tertiary)' }}>({fmt(lyValue, format)})</span>
                    </div>
                </div>
            </div>
        )
    }

    const snapshotLabel = snapshots?.find(s => s.date === selectedSnapshot)?.label

    // ── Widget renderer ─────────────────────────────────────────────
    const renderWidget = (widgetId: string) => {
        switch (widgetId) {
            case 'invTotalUnits':
                return figures ? renderKpi('Total Units on Hand', figures.totalQuantity, figures.lmTotalQuantity, figures.lyTotalQuantity, 'var(--color-primary)') : null
            case 'invUniqueSKUs':
                return figures ? renderKpi('Unique SKUs', figures.uniqueSKUs, figures.lmUniqueSKUs, figures.lyUniqueSKUs, 'var(--color-info)') : null
            case 'invValue':
                return figures ? renderKpi('Inventory Value (Cost)', figures.inventoryValue, figures.lmInventoryValue, figures.lyInventoryValue, 'var(--color-success)', 'currency') : null
            case 'invMoMChange': {
                if (!figures) return null
                const unitChange = figures.totalQuantity - figures.lmTotalQuantity
                const pct = pctChange(figures.totalQuantity, figures.lmTotalQuantity)
                const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat'
                return (
                    <div className="kpi-card" style={{ '--kpi-accent': 'var(--color-warning)' } as any}>
                        <div className="kpi-label">Month-over-Month Change</div>
                        <div className="kpi-value" style={{ color: dir === 'up' ? 'var(--kpi-positive)' : dir === 'down' ? 'var(--kpi-negative)' : undefined }}>
                            {unitChange > 0 ? '+' : ''}{fmt(unitChange, 'int')}
                        </div>
                        <div className="kpi-comparisons"><div className="kpi-comparison">
                            <span className={`kpi-arrow ${dir}`}>{dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–'}</span>
                            <span className={`kpi-pct ${dir}`}>{Math.abs(pct).toFixed(1)}%</span>
                            <span className="comp-label-full">units change</span>
                        </div></div>
                    </div>
                )
            }
            case 'invLevelChart': {
                const settings = widgetSettings['invLevelChart'] || { showUnits: true, showSKUs: false, showValue: false }
                return visibleLevels.length > 0 ? (
                    <InventoryLevelChart
                        data={visibleLevels}
                        settings={settings}
                        selectedSnapshot={selectedSnapshot}
                        onSelectSnapshot={handleSelectSnapshotFromChart}
                    />
                ) : null
            }
            case 'invSnapshotSummary': {
                if (!visibleLevels || visibleLevels.length < 2) return <div className="chart-panel" style={{ padding: 'var(--space-5)' }}><div className="chart-panel-title">Snapshot Summary</div><p style={{ color: 'var(--color-text-tertiary)' }}>Not enough data</p></div>
                const first = visibleLevels[0].totalQuantity, last = visibleLevels[visibleLevels.length - 1].totalQuantity
                const p = pctChange(last, first)
                const maxIdx = visibleLevels.reduce((mi, l, i, arr) => l.totalQuantity > arr[mi].totalQuantity ? i : mi, 0)
                const [y, m] = visibleLevels[maxIdx].period.split('-')
                const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
                return (
                    <div className="chart-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 'var(--space-5)' }}>
                        <div className="chart-panel-title">Snapshot Summary</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', marginTop: 'var(--space-3)' }}>
                            <div><div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Data Points</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)', marginTop: 4 }}>{visibleLevels.length} months</div></div>
                            <div><div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Trend (First → Last)</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: 4, color: p >= 0 ? 'var(--kpi-positive)' : 'var(--kpi-negative)' }}>{p > 0 ? '+' : ''}{p.toFixed(1)}%</div></div>
                            <div><div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Peak Inventory</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)', marginTop: 4 }}>{fmt(Math.max(...visibleLevels.map(l => l.totalQuantity)), 'int')}</div>
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 2 }}>{MONTHS[parseInt(m) - 1]} {y}</div></div>
                        </div>
                    </div>
                )
            }
            case 'invStockouts':
                return stockouts ? <StockoutInsightsPanel items={stockouts} /> : null
            case 'invDeptPie':
                return deptBreakdown ? <DepartmentPieChart data={deptBreakdown} metric="value" /> : null
            default:
                return <div style={{ padding: '1rem', color: 'var(--color-text-tertiary)' }}>Unknown widget: {widgetId}</div>
        }
    }

    const isLoading = !levels && levelsLoading

    if (configLoading) {
        return (<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
            <div className="page-header"><h1>{greeting}, {user?.displayName.split(' ')[0]} 📦</h1></div>
            <div className="dashboard-loading"><div className="spinner" /><span>Loading configuration…</span></div>
        </div>)
    }

    return (
        <>
            {/* ── Page Header ───────────────────────────────────── */}
            <div className="page-header">
                <h1>{greeting}, {user?.displayName.split(' ')[0]} 📦</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
                    <select className="header-store-select" value={storeId} onChange={e => setStoreId(e.target.value)}>
                        {storeConfig?.stores
                            ?.filter(s => s.name !== "VPO Central" && s.name !== "VPO Online eStore")
                            ?.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                        {storeConfig?.hasGroup && <option value="group">My Store Group</option>}
                        {storeConfig?.canViewAll && <option value="all">All Stores</option>}
                    </select>


                    {isLiveSelected ? (
                        <span className="live-indicator">
                            Live Inventory
                        </span>
                    ) : snapshotLabel ? (
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', fontWeight: 400 }}>
                            · Snapshot: {snapshotLabel}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* ── Filter Toolbar ─────────────────────────────────── */}
            <div className="dashboard-toolbar">
                {/* Left Side: Snapshot and Limit Controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <span className="toolbar-label">Snapshot</span>
                    <select value={selectedSnapshot} onChange={e => setSelectedSnapshot(e.target.value)}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 200 }}>
                        {snapshots?.map(s => (<option key={s.date} value={s.date}>{s.label}</option>))}
                    </select>

                    <span className="toolbar-label" style={{ marginLeft: 'var(--space-2)' }}>Months</span>
                    <select value={snapshotLimit} onChange={e => setSnapshotLimit(e.target.value)}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none' }}>
                        {showOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Right Side: Filters & Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginLeft: 'auto', flexWrap: 'wrap' }}>
                    <span className="toolbar-label">Brand</span>
                    <select value={brandId} onChange={e => setBrandId(e.target.value)}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 200 }}>
                        <option value="">All Brands</option>
                        {brands?.map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
                    </select>

                    <span className="toolbar-label">Department</span>
                    <select value={department} onChange={e => { setDepartment(e.target.value); setSubdept(''); setGender('') }}
                        style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 200 }}>
                        <option value="">All Departments</option>
                        {departments?.map(d => (<option key={d} value={d}>{d}</option>))}
                    </select>

                    {department && subdepartments && subdepartments.length > 0 && (<>
                        <span className="toolbar-label">Sub-Dept</span>
                        <select value={subdept} onChange={e => setSubdept(e.target.value)}
                            style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 180 }}>
                            <option value="">All</option>
                            {subdepartments.map(s => (<option key={s} value={s}>{s}</option>))}
                        </select>
                    </>)}

                    {department && genders && genders.length > 0 && (<>
                        <span className="toolbar-label">Gender</span>
                        <select value={gender} onChange={e => setGender(e.target.value)}
                            style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', cursor: 'pointer', outline: 'none', maxWidth: 160 }}>
                            <option value="">All</option>
                            {genders.map(g => (<option key={g} value={g}>{g}</option>))}
                        </select>
                    </>)}

                    {!isEditing ? (
                        <div className="desktop-actions-menu-container" style={{ position: 'relative' }}>
                            <button className="toolbar-refresh-btn" onClick={() => setActionMenuOpen(x => !x)} title="Dashboard Actions">⚙ Actions</button>
                            {actionMenuOpen && (<>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setActionMenuOpen(false)} />
                                <div className="dashboard-actions-menu">
                                    {dataUpdatedAt > 0 && (<div style={{ padding: '8px 12px', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' }}>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</div>)}
                                    <button className="nav-item" onClick={() => { refresh(); setActionMenuOpen(false) }} style={{ padding: '8px 12px' }}><span className="nav-icon">↻</span><span className="nav-label">Refresh</span></button>
                                    <button className="nav-item" onClick={() => { setEditing(true); setActionMenuOpen(false) }} style={{ padding: '8px 12px' }}><span className="nav-icon">✏️</span><span className="nav-label">Edit Layout</span></button>
                                    {isAdmin && (<button className="nav-item" onClick={() => { setShowDebug(true); setActionMenuOpen(false) }} style={{ padding: '8px 12px', color: 'var(--color-text-tertiary)' }}><span className="nav-icon">🐛</span><span className="nav-label">Debug SQL</span></button>)}
                                    {isAdmin && (<button className="nav-item" onClick={() => { handleFlushCache(); setActionMenuOpen(false) }} style={{ padding: '8px 12px', color: 'var(--color-warning)' }}><span className="nav-icon">🗑</span><span className="nav-label">Flush Cache</span></button>)}
                                </div>
                            </>)}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            <button className="edit-mode-btn" onClick={() => setShowGallery(true)}>➕ Add Widget</button>
                            <LayoutPresetsMenu {...layoutUi.menuProps} onExport={handleExportLayout} onImport={handleImportLayout} />
                            <button className="edit-mode-btn cancel" onClick={handleCancel}>✕ Cancel</button>
                            <button className="edit-mode-btn" onClick={layoutUi.openSaveDialog}>Save as…</button>
                            <button className="edit-mode-btn done" onClick={layoutUi.quickSave} disabled={layoutUi.busy}>{layoutUi.busy ? 'Saving…' : '💾 Save'}</button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Widget Gallery ────────────────────────────────── */}
            {showGallery && <WidgetGallery onClose={() => setShowGallery(false)} dashboardType="inventory" />}

            {/* ── Save Layout dialog, Revert confirmation ─────────── */}
            {layoutUi.dialogs}

            {/* ── Debug Modal ───────────────────────────────────────── */}
            {showDebug && <SqlDebugModal queries={debugData?.debug ?? []} activeProfile={undefined} connectionInfo={undefined} onClose={() => setShowDebug(false)} />}

            {layoutUi.banner}

            {/* ── Loading State ──────────────────────────────────── */}
            {isLoading && (<div className="dashboard-loading"><div className="spinner" /><span>Loading inventory data…</span></div>)}

            {/* ── Widget Grid ────────────────────────────────────── */}
            {!isLoading && (
                <WidgetGrid
                    layouts={layouts} activeWidgets={activeWidgets} widgetSettings={widgetSettings}
                    isEditing={isEditing} layoutVersion={layoutVersion}
                    setLayouts={setLayouts} snapshotLayouts={snapshotLayouts} removeWidget={removeWidget}
                    updateWidgetSettings={updateWidgetSettings} renderWidget={renderWidget}
                    settingsOpen={settingsOpen} setSettingsOpen={setSettingsOpen}
                    isFetching={levelsLoading || stockoutsLoading || deptLoading}
                />
            )}
        </>
    )
}
